import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { mergeTmdbSeasonsIntoExisting, countEpisodes } from '../../utils/mergeTmdbSeriesSeasons';

const Series = () => {
  const [series, setSeries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: [],
    releaseYear: '',
    director: '',
    cast: [],
    poster: '',
    trailer: '',
    language: ['Français'],
    rating: { imdb: 0, site: 0 },
    isTrending: false,
    isNewRelease: false,
    isAnimation: false,
    seasons: [],
    tmdbReviews: []
  });
  const [tmdbUrl, setTmdbUrl] = useState('');
  const [tmdbLanguage, setTmdbLanguage] = useState('fr-FR');
  const [loadingTmdb, setLoadingTmdb] = useState(false);
  const [episodeForm, setEpisodeForm] = useState({
    episodeNumber: '',
    title: '',
    description: '',
    videoUrl: '',
    videoCode: '',
    duration: '',
    thumbnail: ''
  });

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [seriesPerPage] = useState(10);
  const [filteredSeries, setFilteredSeries] = useState([]);

  useEffect(() => {
    fetchSeries();
  }, []);

  // Filter series based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSeries(series);
    } else {
      const lowercaseSearch = searchTerm.toLowerCase();
      const filtered = series.filter(s => 
        s.title?.toLowerCase().includes(lowercaseSearch) ||
        s.genre?.some(g => g.toLowerCase().includes(lowercaseSearch)) ||
        s.director?.toLowerCase().includes(lowercaseSearch) ||
        s.releaseYear?.toString().includes(searchTerm)
      );
      setFilteredSeries(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, series]);

  const fetchSeries = async () => {
    try {
      const res = await axios.get('/api/admin/series');
      setSeries(res.data);
      setFilteredSeries(res.data);
    } catch (error) {
      console.error('Error fetching series:', error);
    }
  };

  // Get current series for pagination
  const indexOfLastSeries = currentPage * seriesPerPage;
  const indexOfFirstSeries = indexOfLastSeries - seriesPerPage;
  const currentSeries = filteredSeries.slice(indexOfFirstSeries, indexOfLastSeries);
  const totalPages = Math.ceil(filteredSeries.length / seriesPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: parseFloat(value) || 0 }
      }));
    } else if (name === 'genre' || name === 'language') {
      setFormData(prev => ({
        ...prev,
        [name]: value.split(',').map(item => item.trim()).filter(item => item)
      }));
    } else if (name === 'cast') {
      setFormData(prev => ({
        ...prev,
        [name]: value.split(',').map(item => item.trim()).filter(item => item).map(name => ({
          name: name,
          image: ''
        }))
      }));
    } else if (name === 'videoUrl' && episodeForm) {
      // Auto-extract Bunny Stream video ID from URL
      const bunnyMatch = value.match(/iframe\.mediadelivery\.net\/play\/\d+\/([a-f0-9-]+)/i);
      if (bunnyMatch && bunnyMatch[1]) {
        setEpisodeForm(prev => ({
          ...prev,
          [name]: value,
          videoCode: bunnyMatch[1] // Auto-extract video ID (UUID)
        }));
      } else {
        // If it's just a UUID, use it directly
        const uuidMatch = value.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i);
        if (uuidMatch) {
          setEpisodeForm(prev => ({
            ...prev,
            [name]: value,
            videoCode: value // Use UUID directly as video ID
          }));
        } else {
          setEpisodeForm(prev => ({ ...prev, [name]: value }));
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const fetchTmdbData = async () => {
    if (!tmdbUrl || !tmdbUrl.includes('themoviedb.org/tv/')) {
      alert('Veuillez entrer une URL de série TV TMDB valide (ex: https://www.themoviedb.org/tv/12345)');
      return;
    }

    setLoadingTmdb(true);
    try {
      const res = await axios.post('/api/admin/tmdb/fetch-series', { 
        url: tmdbUrl,
        language: tmdbLanguage 
      });
      const tmdbData = res.data;

      let addedEpisodes = 0;
      setFormData((prev) => {
        const mergedSeasons = mergeTmdbSeasonsIntoExisting(
          prev.seasons || [],
          tmdbData.seasons || []
        );
        const before = countEpisodes(prev.seasons);
        const after = countEpisodes(mergedSeasons);
        addedEpisodes = Math.max(0, after - before);

        return {
          ...prev,
          title: tmdbData.title || prev.title,
          description: tmdbData.description || prev.description,
          genre: tmdbData.genre && tmdbData.genre.length > 0 ? tmdbData.genre : prev.genre,
          releaseYear: tmdbData.releaseYear || prev.releaseYear,
          director: tmdbData.director || prev.director,
          cast: tmdbData.cast && tmdbData.cast.length > 0 ? tmdbData.cast : prev.cast,
          poster: tmdbData.poster || prev.poster,
          trailer: tmdbData.trailer || prev.trailer,
          language: ['Français'],
          rating: {
            imdb: tmdbData.rating?.imdb || prev.rating.imdb,
            site: tmdbData.rating?.site || prev.rating.site
          },
          seasons: mergedSeasons,
          tmdbReviews:
            tmdbData.tmdbReviews && tmdbData.tmdbReviews.length
              ? tmdbData.tmdbReviews
              : prev.tmdbReviews || []
        };
      });

      alert(
        editingSeries
          ? `Série actualisée depuis TMDB. Les liens vidéo Bunny existants sont conservés.${
              addedEpisodes ? ` ${addedEpisodes} nouvel(le)(s) épisode(s) détecté(s).` : ''
            }`
          : `Données chargées depuis TMDB ! Vous pouvez modifier les champs avant d'enregistrer.`
      );
    } catch (error) {
      console.error('TMDB fetch error:', error);
      alert(error.response?.data?.message || 'Échec du chargement des données de la série depuis TMDB');
    } finally {
      setLoadingTmdb(false);
    }
  };

  const addSeason = () => {
    const seasonNumber = formData.seasons.length > 0 
      ? Math.max(...formData.seasons.map(s => s.seasonNumber)) + 1 
      : 1;
    setFormData(prev => ({
      ...prev,
      seasons: [...prev.seasons, { seasonNumber, episodes: [] }]
    }));
  };

  const deleteSeason = (seasonIndex) => {
    setFormData(prev => ({
      ...prev,
      seasons: prev.seasons.filter((_, idx) => idx !== seasonIndex)
    }));
  };

  const addEpisode = (seasonIndex) => {
    const season = formData.seasons[seasonIndex];
    const episodeNumber = season.episodes.length > 0
      ? Math.max(...season.episodes.map(e => e.episodeNumber)) + 1
      : 1;
    
    setFormData(prev => {
      const newSeasons = [...prev.seasons];
      newSeasons[seasonIndex].episodes.push({
        episodeNumber,
        title: `Épisode ${episodeNumber}`,
        description: '',
        videoUrl: '',
        videoCode: '',
        duration: 0,
        thumbnail: ''
      });
      return { ...prev, seasons: newSeasons };
    });
  };

  const updateEpisode = (seasonIndex, episodeIndex, field, value) => {
    setFormData(prev => {
      const newSeasons = [...prev.seasons];
      if (field === 'videoUrl') {
        // Auto-extract Bunny Stream video ID from URL
        const bunnyMatch = value.match(/iframe\.mediadelivery\.net\/play\/\d+\/([a-f0-9-]+)/i);
        if (bunnyMatch && bunnyMatch[1]) {
          newSeasons[seasonIndex].episodes[episodeIndex].videoCode = bunnyMatch[1];
        } else {
          // If it's just a UUID, use it directly
          const uuidMatch = value.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i);
          if (uuidMatch) {
            newSeasons[seasonIndex].episodes[episodeIndex].videoCode = value;
          }
        }
      }
      newSeasons[seasonIndex].episodes[episodeIndex][field] = value;
      return { ...prev, seasons: newSeasons };
    });
  };

  const deleteEpisode = (seasonIndex, episodeIndex) => {
    setFormData(prev => {
      const newSeasons = [...prev.seasons];
      newSeasons[seasonIndex].episodes = newSeasons[seasonIndex].episodes.filter((_, idx) => idx !== episodeIndex);
      return { ...prev, seasons: newSeasons };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, isAnimation: !!formData.isAnimation };
      if (tmdbUrl.trim()) payload.tmdbUrl = tmdbUrl.trim();
      if (editingSeries) {
        await axios.put(`/api/admin/series/${editingSeries._id}`, payload);
      } else {
        await axios.post('/api/admin/series', payload);
      }
      setShowForm(false);
      setEditingSeries(null);
      setFormData({
        title: '',
        description: '',
        genre: [],
        releaseYear: '',
        director: '',
        cast: [],
        poster: '',
        trailer: '',
        language: ['Français'],
        rating: { imdb: 0, site: 0 },
        isTrending: false,
        isNewRelease: false,
        isAnimation: false,
        seasons: [],
        tmdbReviews: []
      });
      setTmdbUrl('');
      fetchSeries();
    } catch (error) {
      console.error('Error saving series:', error);
      alert(error.response?.data?.message || 'Échec de l\'enregistrement de la série');
    }
  };

  const handleEdit = (series) => {
    setEditingSeries(series);
    setFormData({
      title: series.title || '',
      description: series.description || '',
      genre: series.genre || [],
      releaseYear: series.releaseYear || '',
      director: series.director || '',
      cast: series.cast || [],
      poster: series.poster || '',
      trailer: series.trailer || '',
      language: series.language || [],
      rating: series.rating || { imdb: 0, site: 0 },
      isTrending: series.isTrending || false,
      isNewRelease: series.isNewRelease || false,
      isAnimation: series.isAnimation || false,
      seasons: series.seasons || [],
      tmdbReviews: series.tmdbReviews || []
    });
    setTmdbUrl(series.tmdbUrl || '');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette série ?')) return;
    try {
      await axios.delete(`/api/admin/series/${id}`);
      fetchSeries();
    } catch (error) {
      console.error('Error deleting series:', error);
      alert('Échec de la suppression de la série');
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Gérer les Séries</h1>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingSeries(null);
              setFormData({
                title: '',
                description: '',
                genre: [],
                releaseYear: '',
                director: '',
                cast: [],
                poster: '',
                trailer: '',
                language: ['Français'],
                rating: { imdb: 0, site: 0 },
                isTrending: false,
                isNewRelease: false,
                isAnimation: false,
                seasons: [],
                tmdbReviews: []
              });
              setTmdbUrl('');
            }}
            className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
          >
            + Ajouter une Série
          </button>
        </div>

        {showForm && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingSeries ? 'Modifier la Série' : 'Ajouter une Nouvelle Série'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* TMDB: import or refresh (merge keeps Bunny video IDs on existing episodes) */}
              <div className="bg-gray-700 p-4 rounded-lg mb-4 border border-gray-600">
                <label className="block text-white mb-2 font-semibold">
                  🎬 The Movie Database (TMDB)
                </label>
                <p className="text-gray-400 text-sm mb-3">
                  {editingSeries
                    ? "Collez l'URL TMDB de la série (elle est enregistrée pour les prochaines fois). Cliquez sur « Actualiser » pour récupérer l'affiche, les métadonnées et les nouveaux épisodes — les liens vidéo Bunny déjà renseignés ne sont pas effacés."
                    : "Collez l'URL TMDB pour préremplir la fiche. Vous pourrez enregistrer l'URL avec la série pour actualiser plus tard."}
                </p>
                <div className="flex flex-wrap gap-2 mb-2">
                  <input
                    type="url"
                    value={tmdbUrl}
                    onChange={(e) => setTmdbUrl(e.target.value)}
                    placeholder="https://www.themoviedb.org/tv/1396-..."
                    className="flex-1 min-w-[200px] px-4 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                  />
                  <select
                    value={tmdbLanguage}
                    onChange={(e) => setTmdbLanguage(e.target.value)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="fr-FR">🇫🇷 Français</option>
                    <option value="en-US">🇺🇸 Anglais</option>
                    <option value="es-ES">🇪🇸 Espagnol</option>
                    <option value="de-DE">🇩🇪 Allemand</option>
                  </select>
                  <button
                    type="button"
                    onClick={fetchTmdbData}
                    disabled={loadingTmdb || !tmdbUrl}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {loadingTmdb
                      ? 'Chargement...'
                      : editingSeries
                        ? 'Actualiser depuis TMDB'
                        : 'Remplir depuis TMDB'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Titre *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Année de sortie *</label>
                  <input
                    type="number"
                    name="releaseYear"
                    value={formData.releaseYear}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-white mb-2">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">Genre (séparés par des virgules) *</label>
                  <input
                    type="text"
                    name="genre"
                    value={formData.genre.join(', ')}
                    onChange={handleInputChange}
                    placeholder="Action, Drame, Science-fiction"
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">Réalisateur/Créateur *</label>
                  <input
                    type="text"
                    name="director"
                    value={formData.director}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2">URL de l'affiche *</label>
                  <input
                    type="url"
                    name="poster"
                    value={formData.poster}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white mb-2">URL de la bande-annonce</label>
                  <input
                    type="url"
                    name="trailer"
                    value={formData.trailer}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg"
                  />
                </div>
              </div>

              {/* Seasons & Episodes */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-white font-semibold">Saisons & Épisodes *</label>
                  <button
                    type="button"
                    onClick={addSeason}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    + Ajouter une Saison
                  </button>
                </div>

                {formData.seasons.map((season, seasonIndex) => (
                  <div key={seasonIndex} className="bg-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold">Saison {season.seasonNumber}</h3>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => addEpisode(seasonIndex)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          + Épisode
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSeason(seasonIndex)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                        >
                          Supprimer la Saison
                        </button>
                      </div>
                    </div>

                    {season.episodes.map((episode, episodeIndex) => (
                      <div key={episodeIndex} className="bg-gray-600 rounded p-3 mb-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <input
                            type="number"
                            value={episode.episodeNumber}
                            onChange={(e) => updateEpisode(seasonIndex, episodeIndex, 'episodeNumber', parseInt(e.target.value))}
                            placeholder="Épisode #"
                            className="px-3 py-2 bg-gray-700 text-white rounded text-sm"
                            required
                          />
                          <input
                            type="text"
                            value={episode.title}
                            onChange={(e) => updateEpisode(seasonIndex, episodeIndex, 'title', e.target.value)}
                            placeholder="Titre de l'épisode"
                            className="px-3 py-2 bg-gray-700 text-white rounded text-sm"
                            required
                          />
                        </div>
                        <textarea
                          value={episode.description || ''}
                          onChange={(e) => updateEpisode(seasonIndex, episodeIndex, 'description', e.target.value)}
                          placeholder="Description de l'épisode"
                          rows="2"
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded text-sm mb-2"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                          <input
                            type="url"
                            value={episode.videoUrl || ''}
                            onChange={(e) => updateEpisode(seasonIndex, episodeIndex, 'videoUrl', e.target.value)}
                            placeholder="URL Bunny Stream ou ID vidéo (ex: 2449ac8c-9d76-42b7-b044-9057c839df5c)"
                            className="px-3 py-2 bg-gray-700 text-white rounded text-sm"
                          />
                          <input
                            type="number"
                            value={episode.duration || ''}
                            onChange={(e) => updateEpisode(seasonIndex, episodeIndex, 'duration', parseInt(e.target.value))}
                            placeholder="Durée (minutes)"
                            className="px-3 py-2 bg-gray-700 text-white rounded text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => deleteEpisode(seasonIndex, episodeIndex)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                          >
                            Supprimer
                          </button>
                        </div>
                        {episode.videoCode && (
                          <p className="text-green-400 text-xs">✅ Code : {episode.videoCode}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="bg-gray-700 p-4 rounded-lg">
                <label className="block text-white mb-2">Langue</label>
                <input
                  type="text"
                  name="language"
                  value="Français"
                  className="w-full px-4 py-2 bg-gray-600 text-gray-400 rounded-lg cursor-not-allowed"
                  disabled
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isTrending"
                    id="isTrending"
                    checked={formData.isTrending}
                    onChange={(e) => setFormData(prev => ({ ...prev, isTrending: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isTrending" className="text-white cursor-pointer">
                    Afficher sur la page d'accueil (Tendances)
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isNewRelease"
                    id="isNewRelease"
                    checked={formData.isNewRelease}
                    onChange={(e) => setFormData(prev => ({ ...prev, isNewRelease: e.target.checked }))}
                    className="w-5 h-5 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                  />
                  <label htmlFor="isNewRelease" className="text-white cursor-pointer">
                    Afficher comme Nouveauté
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isAnimation"
                    id="isAnimation"
                    checked={formData.isAnimation}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAnimation: e.target.checked }))}
                    className="w-5 h-5 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="isAnimation" className="text-white cursor-pointer">
                    Animation (utilise la bibliothèque Animations Bunny)
                  </label>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                >
                  {editingSeries ? 'Mettre à jour la Série' : 'Ajouter la Série'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSeries(null);
                  }}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Series List */}
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Rechercher des séries par titre, genre, réalisateur ou année..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="text-gray-400 text-sm">
                {filteredSeries.length} série{filteredSeries.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-white">Titre</th>
                  <th className="px-6 py-4 text-left text-white">Année</th>
                  <th className="px-6 py-4 text-left text-white">Saisons</th>
                  <th className="px-6 py-4 text-left text-white">Tendances</th>
                  <th className="px-6 py-4 text-left text-white">Nouveauté</th>
                  <th className="px-6 py-4 text-left text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentSeries.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                      {searchTerm ? 'Aucune série trouvée correspondant à votre recherche.' : 'Aucune série disponible. Ajoutez votre première série !'}
                    </td>
                  </tr>
                ) : (
                  currentSeries.map((s) => (
                    <tr key={s._id} className="border-b border-gray-700">
                      <td className="px-6 py-4 text-white">{s.title}</td>
                      <td className="px-6 py-4 text-gray-300">{s.releaseYear}</td>
                      <td className="px-6 py-4 text-gray-300">{s.seasons?.length || 0}</td>
                      <td className="px-6 py-4 text-gray-300">
                        {s.isTrending ? (
                          <span className="px-2 py-1 bg-yellow-500 text-black rounded text-xs font-semibold">Tendances</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {s.isNewRelease ? (
                          <span className="px-2 py-1 bg-green-500 text-black rounded text-xs font-semibold">Nouveau</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(s)}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(s._id)}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <div className="text-gray-400 text-sm">
                Affichage de {indexOfFirstSeries + 1} à {Math.min(indexOfLastSeries, filteredSeries.length)} sur {filteredSeries.length} série{filteredSeries.length !== 1 ? 's' : ''}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Précédent
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`px-4 py-2 rounded-lg transition ${
                          currentPage === pageNum
                            ? 'bg-white text-gray-900 font-semibold'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Series;
